import { FC, memo, useState } from 'react'

import { Text, AccordionItem } from '@cash-me/react-components'
import NextImage from "next/image"

import { Grid, Col } from 'components/grid'
import { pushDataLayer } from 'utils/push-data-layer'

import * as S from './styles'

const TutorialWhatsApp: FC = memo(() => {
  const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(false)
  const sendWhatsAppMessage = () => {
    pushDataLayer({
      category: 'Clique',
      action: 'Sou cliente',
      label: `Botão Solicitar via WhatsApp`
    })

    const anchor = document.createElement('a')
    anchor.href = 'https://api.whatsapp.com/send?phone=551140402550'
    anchor.target = '_blank'
    anchor.click()
  }

  const handleAccordionClick = () => {
    if (!isAccordionOpen)
      pushDataLayer({
        category: 'Clique',
        action: 'Sou cliente',
        label: `Accordion Atenção!`
      })

    setIsAccordionOpen(!isAccordionOpen)
  }

  return (
    <S.TutorialWhatsApp>
      <Grid col={{ phone: 1, tablet: 2 }} gap={{ tablet: 'md', desktop: 'md' }}>
        <Col col={{ tablet: 1, desktop: 10 }} start={{ desktop: 2 }}>
          <S.GroupElements>
            <Col col={{ desktop: 8 }} start={{ desktop: 3 }}>
              <S.Infos>
                <Text as="h2" size="heading2" weight="medium">
                  Sabia que você também pode solicitar a{' '}
                  <Text as="span" size="heading2" weight="medium" color="var(--colors-pink-text-default)">
                    segunda via pelo nosso WhatsApp?
                  </Text>
                </Text>
              </S.Infos>
            </Col>
            <S.ButtonDS size="lg" onClick={sendWhatsAppMessage}>
              Solicitar via WhatsApp
            </S.ButtonDS>
          </S.GroupElements>
        </Col>
        <Col col={{ tablet: 1, desktop: 5 }} start={{ tablet: 2, desktop: 2 }}>
          <S.StepsWrapper>
            <Text size="heading5" color="var(--colors-neutral-text-weak)" weight="bold">
              Confira o passo a passo simplificado que preparamos para você:
            </Text>
            <S.ContentStep>
              <S.IconWrapper>
                <Text size="body1" color="var(--colors-neutral-text-weak)" weight="bold">
                  1
                </Text>
              </S.IconWrapper>
              <S.ContentText>
                <Text size="heading5" color="var(--colors-neutral-text-weak)" weight="bold">
                  Envie uma mensagem
                </Text>
                <Text size="body1" color="var(--colors-neutral-text-weak)">
                  Adicione o número oficial do <b>WhatsApp da CashMe</b>, certifique-se que está falando com o número{' '}
                  <b>(11) 4040-2550</b>;
                </Text>
              </S.ContentText>
            </S.ContentStep>
            <S.ContentStep>
              <S.IconWrapper>
                <Text size="body1" color="var(--colors-neutral-text-weak)" weight="bold">
                  2
                </Text>
              </S.IconWrapper>
              <S.ContentText>
                <Text size="heading5" color="var(--colors-neutral-text-weak)" weight="bold">
                  Informe seus dados
                </Text>
                <Text size="body1" color="var(--colors-neutral-text-weak)">
                  Nos informe o <b>CPF ou CNPJ do titular</b> principal do seu contrato;
                </Text>
              </S.ContentText>
            </S.ContentStep>
            <S.ContentStep>
              <S.IconWrapper>
                <Text size="body1" color="var(--colors-neutral-text-weak)" weight="bold">
                  3
                </Text>
              </S.IconWrapper>
              <S.ContentText>
                <Text size="heading5" color="var(--colors-neutral-text-weak)" weight="bold">
                  Siga as instruções
                </Text>
                <Text size="body1" color="var(--colors-neutral-text-weak)">
                  1. No menu, selecione a opção <b>&quot;2ª via de boleto&quot;</b>;
                  <br />
                  2. E então escolha se deseja <b>&quot;boleto simplificado&quot;</b>
                  ou <b>&quot;código de barras&quot;</b>;
                </Text>
              </S.ContentText>
            </S.ContentStep>
            <S.ContentStep>
              <S.IconWrapper>
                <Text size="body1" color="var(--colors-neutral-text-weak)" weight="bold">
                  4
                </Text>
              </S.IconWrapper>
              <S.ContentText>
                <Text size="heading5" color="var(--colors-neutral-text-weak)" weight="bold">
                  Receba seu Boleto
                </Text>
                <Text size="body1" color="var(--colors-neutral-text-weak)">
                  Em poucos instantes, você receberá a segunda via do seu boleto diretamente no chat do WhatsApp.
                </Text>
              </S.ContentText>
            </S.ContentStep>
          </S.StepsWrapper>
        </Col>
        <Col col={{ tablet: 2, desktop: 5 }} start={{ tablet: 1, desktop: 7 }}>
          <S.ImageWrapper>
            <S.Image>
              <NextImage
                src={`${process.env.NEXT_PUBLIC_SITE_IMAGES_PREFIX}/images/site/duplicate-bill/whatsapp.webp`}
                alt="tela de celular com o aplicativo WhatsApp"
                width="526"
                height="610"
                quality={90}
                sizes="100vw"
                style={{
                  width: "100%",
                  height: "auto"
                }} />
            </S.Image>
          </S.ImageWrapper>
        </Col>
        <Col col={{ tablet: 2, desktop: 10 }} start={{ desktop: 2 }}>
          <S.AccordionDS id="base" type="multiple">
            <div onClick={() => handleAccordionClick()}>
              <AccordionItem
                id="1"
                subtitle="Antes de efetuar o pagamento, confira as seguintes informações, para sua segurança:"
                title="Atenção!"
              >
                <S.AttentionList>
                  <li>
                    <p>
                      <b>Código de barras:</b> verifique se os últimos dígitos do código de barras correspondem ao valor
                      a ser pago. Verifique, ainda, se os primeiros dígitos do código de barras coincidem com o código
                      do banco emissor. É possível consultar a numeração de todas as instituições bancárias no site da
                      FEBRABAN (
                      <b>
                        <a
                          href="https://portal.febraban.org.br/pagina/3164/12/pt-br/associados"
                          rel="noreferrer"
                          target="_blank"
                        >
                          https://portal.febraban.org.br/pagina/3164/12/pt-br/associados
                        </a>
                        ).
                      </b>
                    </p>
                  </li>
                  <li>
                    <p>
                      <b>Beneficiário desconhecido:</b> verifique sempre o nome da empresa que receberá o valor no
                      momento que estiver fazendo o pagamento online ou solicite ao atendente do banco a confirmação no
                      caixa. O beneficiário que está no boleto deve ser o mesmo no momento da transação do pagamento. Em
                      caso de dúvidas, solicite a confirmação do beneficiário para a empresa.
                    </p>
                  </li>
                  <li>
                    <p>
                      <b>Impossibilidade de leitura do código de barras:</b> recomenda-se que, para efetuar o pagamento,
                      você faça a leitura do código de barras através da câmera do celular, garantindo assim que a
                      leitura é compatível à numeração apresentada no código de barras.
                    </p>
                  </li>
                  <li>
                    <p>
                      <b>Confira seus dados pessoais/da sua empresa:</b> confira com atenção seus dados e/ou os dados de
                      sua empresa para garantir que não existe nenhum erro.
                    </p>
                  </li>
                </S.AttentionList>
                <S.TextDS>
                  Lembrando que, para sua segurança, você também tem a opção de emitir o boleto para pagamento através
                  do nosso portal, acessando o site:{' '}
                  <a href="https://site.chronosapp.com.br/planetaserv/login" rel="noreferrer" target="_blank">
                    https://site.chronosapp.com.br/planetaserv/login
                  </a>
                </S.TextDS>
              </AccordionItem>
            </div>
          </S.AccordionDS>
        </Col>
      </Grid>
    </S.TutorialWhatsApp>
  );
})

TutorialWhatsApp.displayName = 'TutorialWhatsApp'

export default TutorialWhatsApp
